<script setup lang="ts">
import type { Interpretation, Suggestion } from '~/types/api'

definePageMeta({ layout: 'flow' })
useHead({ title: 'Search — Regotiate' })

const route = useRoute()
const { $api } = useApi()
const { start } = useSearchBrief()

const query = ref(typeof route.query.q === 'string' ? route.query.q : '')
const { suggestions } = useSearchSuggestions(query)
const error = ref('')
const busy = ref(false)

async function submit(q: string) {
  if (!q) return
  busy.value = true
  error.value = ''
  try {
    const r = await $api<Interpretation>('/api/search/interpret', {
      method: 'POST',
      body: JSON.stringify({ q }),
    })
    start({
      query: q,
      categorySlug: r.category?.slug ?? null,
      categoryName: r.category?.name ?? null,
      summary: r.summary,
      options: r.options,
      ...(r.budget_min != null ? { budgetMin: r.budget_min } : {}),
      ...(r.budget_max != null ? { budgetMax: r.budget_max } : {}),
    })
    await navigateTo('/find/summary')
  } catch (err) {
    error.value = (err as Error).message
  } finally {
    busy.value = false
  }
}

function pick(s: Suggestion) {
  if (s.category_slug) return navigateTo(`/find/${s.category_slug}`)
  query.value = s.label
  return submit(s.label)
}
</script>

<template>
  <div class="container container--narrow page">
    <BackLink />
    <h1 class="h1 title">Find discounts on products you love by the power of AI</h1>
    <SearchBox v-model="query" autofocus @submit="submit" />
    <p v-if="error" class="form-error msg">{{ error }}</p>
    <p v-if="busy" class="text-sm muted msg" role="status">Understanding your search…</p>

    <section v-if="suggestions.length" class="suggest" aria-live="polite">
      <h2 class="text-sm muted">Suggested search</h2>
      <div class="chips">
        <button v-for="s in suggestions" :key="`${s.type}-${s.label}`" type="button" class="chip" @click="pick(s)">
          {{ s.label }}
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
.title {
  margin: 16px 0 24px;
}

.msg {
  margin-top: 12px;
}

.suggest {
  margin-top: 32px;
}

.suggest h2 {
  margin-bottom: 12px;
}
</style>
